import type { ActorType, OrderStatus } from './enums.ts';

/**
 * The order state machine, mirrored from `order_transitions` in the database (migration 0008).
 * The database enforces it; this copy lets clients know which buttons to show. A test in
 * apps/api asserts the two never diverge.
 */
export interface Transition {
  from: OrderStatus;
  to: OrderStatus;
  actors: readonly ActorType[];
}

const t = (from: OrderStatus, to: OrderStatus, actors: readonly ActorType[]): Transition => ({
  from,
  to,
  actors,
});

export const ORDER_TRANSITIONS: readonly Transition[] = [
  t('DRAFT', 'AWAITING_PAYMENT', ['SYSTEM']),
  t('DRAFT', 'PAYMENT_AUTHORIZED', ['SYSTEM']),
  t('DRAFT', 'CANCELED', ['SYSTEM', 'CUSTOMER', 'STAFF']),
  t('AWAITING_PAYMENT', 'PAYMENT_AUTHORIZED', ['WEBHOOK', 'SYSTEM']),
  t('AWAITING_PAYMENT', 'SCHEDULED', ['WEBHOOK', 'SYSTEM']),
  t('AWAITING_PAYMENT', 'PAYMENT_FAILED', ['WEBHOOK', 'SYSTEM']),
  t('AWAITING_PAYMENT', 'CANCELED', ['CUSTOMER', 'STAFF', 'SYSTEM']),
  t('AWAITING_PAYMENT', 'MANUAL_REVIEW', ['SYSTEM', 'STAFF']),
  t('PAYMENT_FAILED', 'AWAITING_PAYMENT', ['CUSTOMER', 'SYSTEM']),
  t('PAYMENT_FAILED', 'PAYMENT_AUTHORIZED', ['WEBHOOK', 'SYSTEM']),
  t('PAYMENT_FAILED', 'CANCELED', ['CUSTOMER', 'STAFF', 'SYSTEM']),
  t('PAYMENT_FAILED', 'MANUAL_REVIEW', ['SYSTEM', 'STAFF']),
  t('PAYMENT_AUTHORIZED', 'CONFIRMED', ['SYSTEM', 'WEBHOOK']),
  t('PAYMENT_AUTHORIZED', 'CANCELED', ['CUSTOMER', 'STAFF', 'SYSTEM']),
  t('PAYMENT_AUTHORIZED', 'MANUAL_REVIEW', ['SYSTEM', 'STAFF']),
  t('SCHEDULED', 'PAYMENT_AUTHORIZED', ['SYSTEM', 'WEBHOOK']),
  t('SCHEDULED', 'PAYMENT_FAILED', ['SYSTEM', 'WEBHOOK']),
  t('SCHEDULED', 'CANCELED', ['CUSTOMER', 'STAFF', 'SYSTEM']),
  t('SCHEDULED', 'MANUAL_REVIEW', ['SYSTEM', 'STAFF']),
  t('CONFIRMED', 'QUEUED', ['SYSTEM', 'STAFF']),
  t('CONFIRMED', 'CANCELED', ['CUSTOMER', 'STAFF', 'SYSTEM']),
  t('CONFIRMED', 'MANUAL_REVIEW', ['SYSTEM', 'STAFF']),
  t('QUEUED', 'PREPARING', ['STAFF']),
  t('QUEUED', 'CANCELED', ['CUSTOMER', 'STAFF', 'SYSTEM']),
  t('QUEUED', 'MANUAL_REVIEW', ['SYSTEM', 'STAFF']),
  t('PREPARING', 'IN_OVEN', ['STAFF']),
  t('PREPARING', 'REMAKE', ['STAFF']),
  t('PREPARING', 'CANCEL_REQUESTED', ['CUSTOMER']),
  t('PREPARING', 'NO_SHOW', ['SYSTEM', 'STAFF']),
  t('PREPARING', 'MANUAL_REVIEW', ['SYSTEM', 'STAFF']),
  t('IN_OVEN', 'BOXING', ['STAFF']),
  t('IN_OVEN', 'REMAKE', ['STAFF']),
  t('IN_OVEN', 'CANCEL_REQUESTED', ['CUSTOMER']),
  t('IN_OVEN', 'NO_SHOW', ['SYSTEM', 'STAFF']),
  t('IN_OVEN', 'MANUAL_REVIEW', ['SYSTEM', 'STAFF']),
  t('BOXING', 'READY', ['STAFF']),
  t('BOXING', 'REMAKE', ['STAFF']),
  t('BOXING', 'CANCEL_REQUESTED', ['CUSTOMER']),
  t('BOXING', 'NO_SHOW', ['SYSTEM', 'STAFF']),
  t('BOXING', 'MANUAL_REVIEW', ['SYSTEM', 'STAFF']),
  t('READY', 'PICKED_UP', ['STAFF']),
  t('READY', 'REMAKE', ['STAFF']),
  t('READY', 'CANCEL_REQUESTED', ['CUSTOMER']),
  t('READY', 'NO_SHOW', ['SYSTEM', 'STAFF']),
  t('READY', 'MANUAL_REVIEW', ['SYSTEM', 'STAFF']),
  t('PICKED_UP', 'COMPLETED', ['WEBHOOK', 'SYSTEM', 'STAFF']),
  t('PICKED_UP', 'MANUAL_REVIEW', ['SYSTEM', 'STAFF']),
  t('CANCEL_REQUESTED', 'CANCELED', ['STAFF']),
  t('CANCEL_REQUESTED', 'PREPARING', ['STAFF']),
  t('CANCEL_REQUESTED', 'IN_OVEN', ['STAFF']),
  t('CANCEL_REQUESTED', 'BOXING', ['STAFF']),
  t('CANCEL_REQUESTED', 'READY', ['STAFF']),
  t('CANCEL_REQUESTED', 'MANUAL_REVIEW', ['SYSTEM', 'STAFF']),
  t('REMAKE', 'PREPARING', ['STAFF']),
  t('REMAKE', 'CANCELED', ['STAFF']),
  t('COMPLETED', 'PARTIALLY_REFUNDED', ['STAFF', 'WEBHOOK']),
  t('COMPLETED', 'REFUNDED', ['STAFF', 'WEBHOOK']),
  t('NO_SHOW', 'PARTIALLY_REFUNDED', ['STAFF', 'WEBHOOK']),
  t('NO_SHOW', 'REFUNDED', ['STAFF', 'WEBHOOK']),
  t('CANCELED', 'REFUNDED', ['STAFF', 'WEBHOOK']),
  t('CANCELED', 'PARTIALLY_REFUNDED', ['STAFF', 'WEBHOOK']),
  t('PARTIALLY_REFUNDED', 'REFUNDED', ['STAFF', 'WEBHOOK']),
  t('MANUAL_REVIEW', 'CONFIRMED', ['STAFF']),
  t('MANUAL_REVIEW', 'QUEUED', ['STAFF']),
  t('MANUAL_REVIEW', 'READY', ['STAFF']),
  t('MANUAL_REVIEW', 'CANCELED', ['STAFF']),
  t('MANUAL_REVIEW', 'COMPLETED', ['STAFF']),
  t('MANUAL_REVIEW', 'NO_SHOW', ['STAFF']),
];

export function canTransition(from: OrderStatus, to: OrderStatus, actor: ActorType): boolean {
  return ORDER_TRANSITIONS.some((x) => x.from === from && x.to === to && x.actors.includes(actor));
}

export function nextStatuses(from: OrderStatus, actor: ActorType): OrderStatus[] {
  return ORDER_TRANSITIONS.filter((x) => x.from === from && x.actors.includes(actor)).map(
    (x) => x.to,
  );
}

/** The single kitchen button for each stage. */
export const KITCHEN_NEXT: Partial<Record<OrderStatus, { to: OrderStatus; label: string }>> = {
  QUEUED: { to: 'PREPARING', label: 'START PREP' },
  PREPARING: { to: 'IN_OVEN', label: 'IN OVEN' },
  IN_OVEN: { to: 'BOXING', label: 'BOXING' },
  BOXING: { to: 'READY', label: 'READY' },
  READY: { to: 'PICKED_UP', label: 'PICKED UP' },
  REMAKE: { to: 'PREPARING', label: 'START REMAKE' },
};
